import { BookingEngine, Rates, roomData } from "../../index";
import Selectors from "./Selectors";
import waitForSelectors, { waitForSingleShadowSelectors } from "../../utilities/waitForSelectors";
import spaUrlChanged from "../../utilities/spaUrlChanged";
import { handleIntegrationInjector } from "../../hotelIntegrationHandlers";
import axios from "axios";
import {
  guestbookSites,
  environment,
} from "../../../settings/IntegrationConstants";
import moment from "moment";
import {
  setNativeValue,
  getCurrencySymbol,
  parsePrice,
  getBrowserInfo,
  getDeviceInfo,
} from "../../utilities/utils";

declare global {
  interface Window {
    gb_data: any;
    _globalTracking: any;
    angular: any;
  }
}

const gbData = window.gb_data;

const Skipper: BookingEngine = {
  name: gbData.bookingEngine,
  hotel: {
    name: gbData.hotel.name,
    isAutomated: gbData.hotel.isAutomated,
    rateCodeExclusions: gbData.hotel.rateCodeExclusions,
    packageRateExlusions: gbData.hotel.packageRateExlusions,
    exludeAllGroupCodes: gbData.hotel.exludeAllGroupCodes,
    exludeAllCorpCodes: gbData.hotel.exludeAllCorpCodes,
    whiteLabel: gbData.hotel.whiteLabel,
    dontAbTrack: true,
    memberCode: gbData.hotel.memberCode,
    memberCodeType: gbData.hotel.memberCodeType,
    showEnrollment: JSON.parse(
      sessionStorage.getItem("gbShowEnrollment") || "true"
    ),
    widgetIdentifier: gbData.widget.identifier,
    primaryColor: gbData.widget.primaryColor,
    secondaryColor: gbData.widget.secondaryColor,
    widgetAnchored: gbData.widget.widgetAnchored,
    widgetWidth: gbData.widget.widgetWidth,
    widgetPositionTop: gbData.widget.widgetPositionTop,
    widgetPositionRight: gbData.widget.widgetPositionRight,
    widgetPositionBottom: gbData.widget.widgetPositionBottom,
    widgetPositionLeft: gbData.widget.widgetPositionLeft,
    inheritBeFont: gbData.widget.inheritBeFont,
    desktopEnrollmentPrimaryColor: gbData.widget.desktopEnrollmentPrimaryColor,
    benefits: gbData.widget.benefits,
  },
  selectors: Selectors,
  singlePageApp: true,
  requireResizeListener: true,
  widgetFixedPosition: !gbData.widget.widgetAnchored,
  largeEnrollmentMessage: false,
  activeIntervals: [],
  promoCodes: [],
  // pattern match for future proof. i've come across #skipper-target, #skipper-app
  shadowRoot: document.querySelector("body>div[id^=skipper]")?.shadowRoot, 
  saveInfoAtConfirmation: true,
  mobileTooltip: true,

  /* ADDITIONAL NOTES */
  // can use comment // @ts-ignore to ignore out ts errors

  /**
   * Grab widget target
   * (not usually modified)
   */
  getWidgetTarget: () => {
    return Skipper.shadowRoot?.querySelector(Skipper.selectors.widget.target)
  },
  /**
   * Applies member code to room search at user login
   * Only applicable if we have an existing member code (ExampleBookingEngine.hotel.memberCode)
   * Reference Travelclick.ts or SynxisGTM.ts for fleshed out example
   */
  getMemberCode: () => {
    // ...
  },
  /**
   * Clears out member code at user logout
   */
  clearMemberCode: () => {
    // ...
  },
  /**
   * Grab rates info and target to inject, Also applies rate exclusions if any exist
   * @returns ratesInfo[{rewardsAmount, shouldDisplay, targetElement},{},...] 
   * 
   * select all relevant rate $XX.XX elements, parse out the price, 
   * check for exclusion if needed and, determine where to inject our messaging (messageTarget)
   */
  getRates: () => {
    const ratesInfo: Rates[] = [];
    const rates = Skipper.shadowRoot.querySelectorAll(
      Skipper.selectors.ratesPage.rates
    );
    rates.forEach((rate: any, index: number) => {
      // This is where we'd like our "Earn $" banner message to injected
      const messageTarget = (rate as HTMLElement).parentElement.parentElement.parentElement.parentElement;
      if (messageTarget) {
        const injectedElement = !!messageTarget.querySelector(
          Skipper.selectors.ratesPage.ratesMessage
        )
          ? messageTarget.querySelector(
              Skipper.selectors.ratesPage.ratesMessage
            )
          : document.createElement("div"); // TODO: temp document
        if (
          !messageTarget.querySelector(
            Skipper.selectors.ratesPage.ratesMessage
          ) &&
          injectedElement
        ) {
          injectedElement.setAttribute(
            "class",
            Skipper.selectors.ratesPage.ratesMessage.substr(1)
          );
          messageTarget.appendChild(injectedElement);
        }

        const taxedRate = window.gb_data.hotel.taxedRate;
        const ratePrice = parsePrice((rate as HTMLElement).textContent);
        let shouldDisplay = true;
        let calculatedRewards = (ratePrice / taxedRate) * 0.15;

        // +++ Use chunk below if implementing package rate exclusions. Can omit if not applicable +++
        const rateCodes = Skipper.getRateTypes
          ? Skipper.getRateTypes()
          : [];
        const packageRateExlusions =
          Skipper.hotel.packageRateExlusions;

        if (packageRateExlusions && rateCodes.length === rates.length) {
          packageRateExlusions.forEach((packageRateExlusion: any) => {
            if (packageRateExlusion.name === rateCodes[index]) {
              if (packageRateExlusion.rate === 0) shouldDisplay = false;
              else {
                calculatedRewards =
                  (ratePrice - packageRateExlusion.rate / taxedRate) * 0.15;
              }
            }
          });
        }
        // +++

        ratesInfo.push({
          rewardsAmount: (calculatedRewards as any).toFixed(0),
          shouldDisplay: shouldDisplay,
          target: injectedElement,
        });
      }
    });
    sessionStorage.setItem("rates", JSON.stringify(ratesInfo));
    return ratesInfo;
  },
  /**
   * Grab the confirmation numbers on the final booking page
   * @returns confimationNumbers[], array used to account for multiple room bookings
   */
  getConfirmationNumber: () => {
    let confimationNumbers = [] as string[];
    Skipper.shadowRoot
      .querySelectorAll(
        Skipper.selectors.confirmationPage.confirmationNumbers
      )
      .forEach((element) => {
        confimationNumbers.push(element.textContent);
      });
    return confimationNumbers;
  },
  /**
   * Grab the confirmation message target
   * (where to insert our confirmation message)
   */
  getConfirmationTarget: () =>
    Skipper.shadowRoot?.querySelector(
      Skipper.selectors.confirmationPage
        .confirmationMessageContainer
    ),
  /**
   * Grab displayed rates page currency symbol
   * Try to use window variables, url params, or scrape
   * can use:
   *    .textContent.trim().replace(/[\d\., ]/g, '');
   *    if we need to parse out price
   */
  getRatesCurrency: () => {
    const roomDetails = JSON.parse(document.querySelector('#skipper-ld-json').textContent);
    let currency = "USD";
    if (roomDetails.rooms) {
      currency = roomDetails.rooms[0].priceCurrency;
    } else if (roomDetails.room) {
      currency = roomDetails.room.offers[0].priceCurrency 
    } else if (roomDetails?.reservationDetails?.offers?.priceCurrency) {
      currency = roomDetails?.reservationDetails?.offers?.priceCurrency
    }

    return getCurrencySymbol(currency);
  },
  /**
   * Grab displayed checkout page currency symbol
   * Try to use window variables, url params, or scrape
   * can use:
   *  .textContent.trim().replace(/[\d\., ]/g, '');
   * if we need to parse out price
   */
  getCheckoutCurrency: () => {
    // return getCurrencySymbol(
    //   Skipper.shadowRoot.querySelector(
    //     Skipper.selectors.paymentPage.currency
    //   )
    // );
    return '$'
  },
  /**
   * Grab currency CODE to save in reservation payload
   * Try to use window variables, url params, or scrape
   */
  getCurrencyToSave: () => {
    return Skipper.shadowRoot.querySelector(
      Skipper.selectors.paymentPage.currency
    );
  },
  /**
   * Grab language code
   */
  getLanguage: () => {
    const targetElement = document.querySelector("html") as HTMLElement;
    const lang = targetElement.getAttribute("lang")?.split("-")[0];
    if (typeof lang === "string") {
      return lang;
    } else {
      return "en";
    }
  },
  /**
   * Grab our enrollment message container
   * (not usually modified)
   */
  getEnrollmentTarget: () =>
    Skipper.shadowRoot.querySelector(
      Skipper.selectors.paymentPage.enrollmentMessageContainer
    ),
  /**
   * Grab reservation check in for reservation payload
   */
  getPaymentPageCheckinDate: () => {
      let checkInText = Skipper.shadowRoot
        .querySelector(Skipper.selectors.paymentPage.checkin)
        .textContent.trim();
      let checkIn = moment(checkInText).format("YYYY-MM-DD");
      return checkIn;
  },
  /**
   * Grab reservation check in for reservation payload
   */
  getPaymentPageCheckoutDate: () => {
      let checkOutText = Skipper.shadowRoot
        .querySelector(Skipper.selectors.paymentPage.checkout)
        .textContent.trim();
      let checkIn = moment(checkOutText).format("YYYY-MM-DD");
      return checkIn;
  },
  /**
   * Prefill user info on payment page if user logged in
   * (not usually modified)
   */
  prefillUserInfo: (firstName = "", lastName = "", email = "") => {
    waitForSelectors(Skipper.selectors.paymentPage.waitFor, () => {
      const emailInput = (document.querySelector(
        Skipper.selectors.paymentPage.emailInput
      ) as HTMLInputElement);
      const firstNameInput = (document.querySelector(
        Skipper.selectors.paymentPage.firstNameInput
      ) as HTMLInputElement);
      const lastNameInput = (document.querySelector(
        Skipper.selectors.paymentPage.lastNameInput
      ) as HTMLInputElement);
      const event = new Event("input", { bubbles: true });

      setNativeValue(firstNameInput, firstName);
      firstNameInput.focus();
      firstNameInput.dispatchEvent(event);
      firstNameInput.blur();

      setNativeValue(lastNameInput, lastName);
      lastNameInput.focus();
      lastNameInput.dispatchEvent(event);
      lastNameInput.blur();

      setNativeValue(emailInput, email);
      emailInput.focus();
      emailInput.dispatchEvent(event);
      emailInput.blur();

      spaUrlChanged(() => {
        Skipper.prefillUserInfo(firstName, lastName, email);
      });
    },
    Skipper.shadowRoot
  );
  },
  /**
   * Grab email from payment page input
   * (not usually modified)
   */
  getGuestEmail: () =>
    Skipper.shadowRoot
      .querySelector(Skipper.selectors.paymentPage.email)
      .value.trim(),
  /**
   * Grab first name from payment page input
   * (not usually modified)
   */
  getGuestFirstName: () =>
    Skipper.shadowRoot
      .querySelector(Skipper.selectors.paymentPage.firstname)
      .value.trim(),
  /**
   * Grab last name from payment page input
   * (not usually modified)
   */
  getGuestLastName: () =>
    Skipper.shadowRoot
      .querySelector(Skipper.selectors.paymentPage.lastname)
      .value.trim(),
  /**
   * Grab rate TYPE from payment page cart
   * Should be a name like "Best Available" if possible and not just "BAR"
   */
  getRateType: () => {
    let rateNames: string[] = [];
    let rates = Skipper.shadowRoot.querySelectorAll(
      Skipper.selectors.paymentPage.rateNames
    );
    rates.forEach((item: any) => {
      rateNames.push(item.textContent);
    });

    return rateNames;
  },
  /**
   * Grab room name for reservation payload
   */
  getRoomName: () => {
    let roomNames: string[] = [];
    let rooms = Skipper.shadowRoot.querySelectorAll(
      Skipper.selectors.paymentPage.roomNames
    );
    rooms.forEach((item: any) => {
      roomNames.push(item.textContent);
    });

    return roomNames;
  },
  /**
   * Grab number of adults, used in package rate exclusion calculations
   */
  getNumberOfAdults: () => {
    let adults = Skipper.shadowRoot.querySelector(
      Skipper.selectors.paymentPage.adults
    );
    return adults;
  },
  /**
   * Grab number of days, used in package rate exclusion calculations
   */
  getNumberOfDays: () => {
    let days = Skipper.shadowRoot.querySelector(
      Skipper.selectors.paymentPage.adults
    );
    return days;
  },
  /**
   * Grab list of rate codes, used in package rate exclusion calculations
   */
  getRateTypes: () => {
    let rateNames: string[] = [];
    let rates = Skipper.shadowRoot.querySelectorAll(
      Skipper.selectors.ratesPage.ratesCodes
    );
    rates.forEach((item: any) => {
      rateNames.push(item.textContent.trim());
    });
    return rateNames;
  },
  /**
   * Multiple use cases
   * 1) Grab all promo/group codes applied
   * If multiple can be applied at once, store them all to ExampleBookingEngine.promoCodes
   * then retrieve them in getAllPromoCodes
   *
   * 2) just return the one code if distincy
   */
  getPromoCode: () => {
    let promoCode = "";
    // return Skipper.shadowRoot.querySelector(ExampleBookingEngine.selectors.paymentPage.promoCode).innerText;
    return promoCode;
  },
  getGroupCode: () => {
    let groupCode = "";
    // return Skipper.shadowRoot.querySelector(ExampleBookingEngine.selectors.paymentPage.groupCode).innerText;
    return groupCode;
  },
  getAllPromoCodes: () => {
    return Skipper.promoCodes
      ? Skipper.promoCodes.join(", ")
      : "";
  },
  /**
   * Grab subtotal at checkout to save
   */
  getSubtotal: () => {
      // Single Room

      /* simplest case:
        let subtotal = parsePrice((Skipper.shadowRoot.querySelector(ExampleBookingEngine.selectors.paymentPage.subtotal) as HTMLElement).innerText);
        const taxedRate = window.gb_data.hotel.taxedRate;
        return Number((subtotal / taxedRate).toFixed(2));
      */

      // Multi Room
      const taxedRate = window.gb_data.hotel.taxedRate;
      let subtotals: number[] = [];
      const numberOfDays = Skipper.getNumberOfDays
        ? Skipper.getNumberOfDays()
        : [1];
      const numberOfAdults = Skipper.getNumberOfAdults
        ? Skipper.getNumberOfAdults()
        : 1;
      
      // also check for datalayer or window var
      const cart = Skipper.shadowRoot.querySelector(
        Skipper.selectors.paymentPage.cartTarget
      );

      cart.forEach((item: any, index: number) => {
        let isRateExcluded;
        let ratePlanName;
        let ratePlanCode;
        let preTaxedTotal;

        // Scrape and set ratePlanName or ratePlanCode
        // ...

        // Scrape pretaxed total per item if applicable
        // ...

        // Check exclusion using rate plan name or rate plan code
        if (Skipper.isRateExcluded) {
          isRateExcluded = Skipper.isRateExcluded(ratePlanCode); 
          // or ratePlanName
        }

        if (isRateExcluded) {
          if (Skipper.getRateExcluded) {
            const rateExcluded = Skipper.getRateExcluded(
              ratePlanCode
            ); 
            // or ratePlanName

            if (rateExcluded) {
              if (rateExcluded.rate === "0.0") {
                subtotals.push(0);
              } else if (rateExcluded.per_day && rateExcluded.rate !== "0.0") {
                // if only one stay length/number of days per reservation, use numberOfDays[0]
                // reference Synxis if cart items include tax
                subtotals.push(
                  Number(
                    (
                      preTaxedTotal -
                      rateExcluded.rate * numberOfDays[index]
                    ).toFixed(2)
                  )
                );
              } else if (
                rateExcluded.per_person &&
                rateExcluded.rate !== "0.0"
              ) {
                // reference Synxis if cart items include tax
                subtotals.push(
                  Number(
                    (
                      preTaxedTotal -
                      rateExcluded.rate * numberOfAdults
                    ).toFixed(2)
                  )
                );
              }
            }
          }
        } else {
          subtotals.push(preTaxedTotal);
          // or if taxed item,
          // subtotals.push(Number((preTaxedTotal / taxedRate).toFixed(2)));
        }
      });

      return subtotals;
  },
  /**
   * Grab grand total with taxes and fees at checkout to save
   */
  getTotalWithTaxes: () => {
      // Example multiroom
      let grandTotals: number[] = [];

      // also check for datalayer or window var
      const cart = Skipper.shadowRoot.querySelector(
        Skipper.selectors.paymentPage.cartTarget
      );
      
      cart.forEach((item: any) => {
        const total = parsePrice(
          Skipper.shadowRoot.querySelector(
            Skipper.selectors.paymentPage.total
          ).textContent
        );
        grandTotals.push(total);
      });

      return grandTotals;
  },
  /**
   * Save reservation data 
   */
  getReservationData: () => {
    const ldJSON = JSON.parse(document.querySelector('#skipper-ld-json').textContent);
    const reservationDetails = ldJSON.reservationDetails;
    const nightsEl = Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.nights);
    const nights = parseInt(nightsEl?.textContent);
    const total = parsePrice(Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.total).textContent);

    let resData: roomData[] = [];
    resData.push({
      email: reservationDetails.underName.email,
      first_name: reservationDetails.underName.givenName,
      last_name: reservationDetails.underName.familyName,
      confirmation_number: Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.confirmationNumbers).textContent,
      checkin_date: moment(reservationDetails.offers.validFrom).format("YYYY-MM-DD"),
      checkout_date: moment(reservationDetails.offers.validThrough).format("YYYY-MM-DD"),
      rate_type: reservationDetails.offers.name,
      room_name: Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.roomName).textContent,
      currency_code: reservationDetails.offers.priceCurrency,
      lang_code: Skipper.getLanguage(),
      total: (reservationDetails.offers.price * nights).toString(),
      total_with_taxes: total.toString(),
      hotel_identifier: window.gb_data.widget.identifier,
      agreed_enrollment_checked: sessionStorage.getItem(
        "gbEnrolledChecked"
      ) as string,
      ab_version_viewed: sessionStorage.getItem(
        "gbShowEnrollment"
      ) as string,
      browser_info: getBrowserInfo(),
      is_mobile: getDeviceInfo(),
      promo_code: Skipper.getAllPromoCodes
        ? Skipper.getAllPromoCodes()
        : ""
    });

    return resData;
  },
  /**
   * Used by Widget at checkout, EnrollmentMessage, and ConfirmationMessage
   * @returns rewards amount
   */
  getRewards: (percentage: number) => {
    let calculatedRewards = 0;
    // Simple cases (single subtotal, no exclusions), use displaySubtotal
    let subtotal = Skipper.displaySubtotal();
    calculatedRewards = Number((subtotal * percentage).toFixed(2));
    return calculatedRewards.toFixed(2);
  },
  /**
   * checks if messaging should be displayed
   * cross check passed in rate with list of package rate exclusions
   * @param rate: name of rate to match with package exclusion name
   * @returns true or false if rate is excluded
   */
  isRateExcluded: (rate: string) => {
    const packageRateExlusions =
      Skipper.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return true;
      }
    }
    return false;
  },
  /**
   * gets the rate exclusion matching the rate code
   * cross check passed in rate with list of package rate exclusions
   * @param rate: name of rate to match with package exclusion name
   * @returns the packageRateExlusion object associated with the passed in rate name
   */
  getRateExcluded: (rate: string) => {
    const packageRateExlusions =
      Skipper.hotel.packageRateExlusions;
    for (const packageRateExlusion of packageRateExlusions as any) {
      if (rate === packageRateExlusion.name) {
        return packageRateExlusion;
      }
    }
  },
  /**
   * Checks if messaging should be displayed
   * sets rateExcluded in sessionStorage accordingly
   * @returns 
   */
  shouldDisplayMessaging: () => {
    sessionStorage.setItem("rateExcluded", "false");
    // if show enrollment (from AB is set to false)
    if (!JSON.parse(sessionStorage.getItem("gbShowEnrollment") || "true"))
      return false;
    
    const promoCode = Skipper.getPromoCode ? Skipper.getPromoCode() : "";
    const groupCode = Skipper.getGroupCode ? Skipper.getGroupCode() : "";
    const rateCodeExclusions = Skipper.hotel.rateCodeExclusions;
    // blanket exclusions
    if (Skipper.hotel.excludeAllPromoCodes) {
      if (promoCode) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    } else if (Skipper.hotel.exludeAllGroupCodes) {
      if (groupCode) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }
    
    for (const rateCodeExclusion of rateCodeExclusions as any) {
      // Do not show if match exists
      if (
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          promoCode.toLowerCase() ||
        rateCodeExclusion.rate_plan_code.toLowerCase() ===
          groupCode.toLowerCase()
      ) {
        sessionStorage.setItem("rateExcluded", "true");
        return false;
      }
    }
    
    // show messaging if all checks pass
    return true;
  },
  /** Uses waitUntilExists to check if the user should see widget
   * @param handler
   * (not usually modified)
   */
  addWidgetListener: (handler) => {
    
    const timeout = waitForSelectors(
      Skipper.selectors.widget.waitFor,
      handler
    );
    if (Skipper.activeIntervals) {
      Skipper.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on rates page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addRatesPageListener: (handler) => {
    const timeout = waitForSingleShadowSelectors(
      'body>div[id^=skipper]',
      Skipper.selectors.ratesPage.waitFor,
      handler
    );
    if (Skipper.activeIntervals) {
      Skipper.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on payment page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addPaymentPageListener: (handler) => {
    const timeout = waitForSingleShadowSelectors(
      'body>div[id^=skipper]',
      Skipper.selectors.paymentPage.waitFor,
      handler
    );
    if (Skipper.activeIntervals) {
      Skipper.activeIntervals.push(timeout);
    }
  },
  /** Uses waitUntilExists to check if the user is on confirmation page then executes the passed handler function
   * @param handler
   * (not usually modified)
   */
  addConfirmationPageListener: (handler) => {
    const timeout = waitForSingleShadowSelectors(
      'body>div[id^=skipper]',
      Skipper.selectors.confirmationPage.waitFor,
      handler
    );
    if (Skipper.activeIntervals) {
      Skipper.activeIntervals.push(timeout);
    }
  },
  /** listen for cancellation number
   * @param handler
   */
  addCancellationPageListener: () => {
    if (Skipper.addCancellationhandler) {
      const timeout = waitForSelectors(
        // modify selectors used based on the BE's cancellation implementation
        Skipper.selectors.cancellation.cancellationTarget,
        Skipper.addCancellationhandler
      );
      if (Skipper.activeIntervals) {
        Skipper.activeIntervals.push(timeout);
      }
    }
  },
  /**
   * submit cancellation to 
   *    /api/v1/submit_booking_engine_cancellation
   */ 
  addCancellationhandler: () => {
    const confirmationNumber = Skipper.shadowRoot.querySelector(
      Skipper.selectors.cancellation.confirmationNumber
    ).textContent;
    const cancellationNumber = Skipper.shadowRoot.querySelector(
      Skipper.selectors.cancellation.cancellationNumber
    ).textContent;
    const today = moment().format("YYYY-MM-DD");

    const submitCancellation = () => {
      if (cancellationNumber) {
        // confirmation_number used to find reservation record in our db
        // cancellation_number is arbitrary
        // in some cases, they are the same
        const cancellationData = {
          confirmation_number: confirmationNumber, 
          cancellation_number: cancellationNumber,
          cancellation_date: today,
          hotel_identifier: window.gb_data.widget.identifier,
        };
        axios
          .post(
            `${
              guestbookSites[environment as string].rewardsdash.url
            }/api/v1/submit_booking_engine_cancellation`,
            cancellationData,
            {
              headers: {
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": "*",
              },
            }
          )
          .then((response) => {
            if (response.data.success) {
              const element = Skipper.shadowRoot.querySelector(
                Skipper.selectors.confirmationPage
                  .confirmationMessageContainer
              );
              if (!!element) {
                element.style.display = "none";
              }
            } else {
              console.log(response);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    };

    submitCancellation();
    // if click/touchstart events needed for sending cancellation
    // Skipper.shadowRoot
    //   .querySelector(ExampleBookingEngine.selectors.cancellationModal.waitFor)
    //   .addEventListener("click", () => submitCancellation());
  },
  /**
   * Creates widget container
   * (not usually modified)
   */
  createWidgetContainer: () => {
    Skipper.shadowRoot
      .querySelector(Skipper.selectors.widget.widget)
      ?.remove();
    if (!Skipper.shadowRoot.querySelector(Skipper.selectors.widget.widget)) {
      const messageTarget = Skipper.shadowRoot.querySelector(
        Skipper.selectors.widget.target
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div"); // TODO: temp document
        injectedElement.id = Skipper.selectors.widget.widget.substr(
          1
        );
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  /**
   * Creates mobile widget container, usually at page head
   * (not usually modified)
   */
  createWidgetMobileContainer: () => {
    Skipper.shadowRoot
      .querySelector(Skipper.selectors.widget.widget)
      ?.remove();
    if (!Skipper.shadowRoot.querySelector(Skipper.selectors.widget.widget)) {
      const messageTarget = Skipper.shadowRoot.querySelector(
        Skipper.selectors.widget.mobileWidgetTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div");
        injectedElement.id = Skipper.selectors.widget.widget.substr(
          1
        );
        messageTarget.insertAdjacentElement("afterend", injectedElement);
      }
    }
  },
  /**
   * Mainly to handle widget scroll behavior
   * Best case, widget reanchors itself to page head underneath navbar on scroll
   * Otherwise it can stay anchored on the page bottom on mobile
   * reference Synxis for scroll and Guestline/Guestdesk for bottom anchored
   */
  additionalWidgetFunctions: () => {
    if (window.innerWidth <= 768) {
      if (
        Skipper.shadowRoot.querySelectorAll("#gb-override-styles-widget")
          .length === 0
      ) {
        const style = document.createElement("style");
        style.setAttribute(
          "id",
          "gb-override-styles-widget"
        );

        style.textContent = `
        /* GB ELEMENT OVERRIDES */
        #guestbook-widget{
          z-index: 2147483647 !important;
          background-color: rgb(240, 242, 244) !important;
          border-radius: 5px !important;
        }
        .outer-accordion-summary {
          justify-content: space-between !important;
          min-height: unset !important;
          color: #${gbData.widget.secondaryColor} !important;
          padding: 5px 20px !important;
          right: 0 !important;
          z-index: 2 !important;
          background-color: #${gbData.widget.primaryColor}!important;
          margin: 0 !important;
          position: relative !important;
          top: 0 !important;
          box-shadow: none !important;
        
          .MuiSvgIcon-root {
            fill: #ffffff !important;
          }
        }
        .MuiAccordionSummary-content div:first-child{
          display: flex;
          align-items: center;
        }

        .gb-widget-header-logo {
          display: flex !important;
          flex-direction: column !important;
          justify-content: center !important;
          margin-right: 12px !important;

          svg {
            height: 30px !important;
            width: 32px !important;
          }
        }
        .button-container {
          display: flex;
          justify-content: center !important;
          margin-top: 10px;
        }
        .gb-signup{
          cursor: pointer !important;
          min-width: unset !important;
          padding: 8px 12px !important;
          font-family: inherit !important;
          font-size: 16px !important;
          line-height: 16px !important;
          font-weight: 500 !important;
          text-transform: uppercase !important;
          width: fit-content !important;
          height: 35px !important;
          margin-right: 12px !important;
          transition: all 0.4s ease-in-out !important;
          border-radius: 0px !important;
          background-color: #${gbData.widget.primaryColor} !important;
          color: #fff !important;
          border-width: 2px !important;
          border-style: solid !important;
          border-color: transparent !important;
        }
        .gb-login{
          cursor: pointer !important;
          min-width: unset !important;
          padding: 8px 12px !important;
          font-family: inherit !important;
          font-size: 16px !important;
          line-height: 16px !important;
          font-weight: 500 !important;
          text-transform: uppercase !important;
          width: fit-content !important;
          height: 35px !important;
          transition: all 0.4s ease-in-out !important;
          border-radius: 0px !important;
          background-color: #fff;
          background-color: rgb(240, 242, 244) !important;
          color: #${gbData.widget.primaryColor} !important;
          border-width: 2px !important;
          border-style: solid !important;
          border-color: #${gbData.widget.primaryColor} !important;
        }
        .gb-login-modal .gb-login-modal-button{
          background-color: #${gbData.widget.primaryColor} !important;
        }
        .gb-login-modal input{
          margin: 0 !important;
        }
        .MuiPaper-root.MuiAccordion-root{
          box-shadow: none !important;
        }
        .MuiButtonBase-root.MuiAccordionSummary-root {
          display: flex !important;
        }
        .inner-accordion-container{
          display: none !important;
        }
        .line{
          display: none !important;
        }
        .MuiAccordionDetails-root {
          padding: 8px 16px 16px !important;
        }
        .MuiAccordionDetails-root a{
          color: #${gbData.widget.primaryColor} !important;
        }

        .MuiCollapse-root.MuiCollapse-hidden{
          visibility: hidden !important;
          height: 0px;
        }

        .MuiIconButton-label{
          width: 1em;
        }
        
        .MuiAccordionSummary-expandIcon{
          display: flex;
          align-items: center;
          transform: rotate(0deg);
          transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        }
        .MuiAccordionSummary-expandIcon.Mui-expanded {
          transform: rotate(180deg);
          transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        }

        .expand-more-icon{
          font-size: 1.5rem !important;
        }
        
        #gb-logout-link{
          border-bottom: 1px solid #444a58 !important;
          cursor: pointer !important;

          &:hover {
            color: #${gbData.widget.primaryColor}!important;
            border-bottom: 2px solid #${gbData.widget.primaryColor} !important;
          }
        }`
        Skipper.shadowRoot.appendChild(style)
      }
    }
  },
  /**
   * Creates enrollment checkbox message container
   * (not usually modified)
   */
  createEnrollmentMessageContainer: () => {
    if (
      !Skipper.shadowRoot.querySelector(
        Skipper.selectors.paymentPage.enrollmentMessageContainer
      )
    ) {
      const enrollmentTarget = Skipper.shadowRoot.querySelector(
        Skipper.selectors.paymentPage.enrollmentTarget
      );
      let messageTarget;
      if (enrollmentTarget) {
        messageTarget = enrollmentTarget;
      }
      if (messageTarget) {
        const injectedElement = document.createElement("div"); // TODO: temp document
        injectedElement.id = Skipper.selectors.paymentPage.enrollmentMessageContainer.substr(
          1
        );
        messageTarget.appendChild(injectedElement);
      }
    }
  },
  /**
   * Creates confirmation message container
   * (not usually modified)
   */
  createConfirmationMessageContainer: () => {
    if (
      !Skipper.shadowRoot.querySelector(
        Skipper.selectors.confirmationPage
          .confirmationMessageContainer
      )
    ) {
      const messageTarget = Skipper.shadowRoot.querySelector(
        Skipper.selectors.confirmationPage.messageTarget
      );
      if (messageTarget) {
        const injectedElement = document.createElement("div"); // TODO: temp document
        injectedElement.id = Skipper.selectors.confirmationPage.confirmationMessageContainer.substr(
          1
        );
        messageTarget.insertAdjacentElement("beforebegin", injectedElement);
      }
    }
  },
  /**
   * Set locale used for messaging translations using
   */
  setLocale: () => {
    window.gb_data.I18n.locale = Skipper.getLanguage();
  },
  /**
   * @return subtotal to use to calculate rewards and display on enrollment
   * can use in other functions to pull payment age subtotal
   */
  displaySubtotal: () => {
    let subtotal = 0;
    const  reservationDetails = JSON.parse(document.querySelector('#skipper-ld-json').textContent).reservationDetails;
    const taxedRate = window.gb_data.hotel.taxedRate;
    let nightsEl = Skipper.shadowRoot.querySelector(Skipper.selectors.paymentPage.nights);

    if (Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.waitFor)) {
      nightsEl = Skipper.shadowRoot.querySelector(Skipper.selectors.confirmationPage.nights);
    }
    if (!reservationDetails?.offers?.price || !nightsEl) {
      return subtotal;
    }

    const nights = parseInt(nightsEl.textContent);
    subtotal = reservationDetails?.offers?.price * nights;
    return Number((subtotal / taxedRate).toFixed(2));;
  },
  /**
   * Triggers session storage reservation details save
   * can add additional listeners if needed
   */
  savePaymentPageInfo: () => {
    const resData = Skipper.getReservationData();
    sessionStorage.setItem("gbBookingDetails", JSON.stringify(resData));
  },
  /**
   * Clears all listeners
   * (not usually modified)
   */
  clearAllListeners: () => {
    if (!Skipper.activeIntervals) return;
    let length = Skipper.activeIntervals.length;
    for (let i = 0; i < length; i++) {
      let int = Skipper.activeIntervals.pop();
      clearInterval(int);
    }
  },
  /**
   * Injects integration
   * (not usually modified)
   */
  injectIntegration: () => {
    handleIntegrationInjector(Skipper);
  },
  /**
   * CSS Overrides for rates page
   */
  additionalRatesPageFunctions: () => {
    // CSS add example
    if (
      Skipper.shadowRoot.querySelectorAll("#gb-override-styles-rates-page")
        .length === 0
    ) {
      Skipper.shadowRoot.querySelector('main').insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-rates-page" type="text/css">
          .gb-rates-message {
            border: none;
          }
          .message-container {
            position: relative;
            width: max-content;
            font-family: "Mark Pro", sans-serif, "Helvetica Neue", sans-serif;
          }
          .gb-banner {
            display: flex;
            padding-top: 0.5em !important;
            font-weight: 600 !important;
            cursor: pointer !important;
            color: #${Skipper.hotel.primaryColor} !important;
            outline: 0 !important;
            &:focus {
              text-decoration: underline;
            }
          }
          .icon-container {
            position: relative;
            display: flex;
            align-items: center;
          }
        </style>`
      );
    }
    if (
      document.querySelectorAll("#gb-override-styles-rates-page")
        .length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-rates-page" type="text/css">
          #gb-hoverover {
            z-index: 2147483647 !important;
          }
        </style>`
      );
    }

  },
  // We'll need to add margin for the enrollment message
  additionalPaymentsPageFunctions: () => {
    if (
      Skipper.shadowRoot.querySelectorAll("#gb-override-styles-payment-page").length === 0
    ) {
      Skipper.shadowRoot.querySelector('main').insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-payment-page" type="text/css">
          #guestbook-enrollment {
            padding: 0 1em;
          }
          .gb-enrollment-container {
            font-family: "Mark Pro", sans-serif, "Helvetica Neue", sans-serif;
            display: block;
            justify-content: space-between !important;
            border: 1px solid #e4e4e4;
            padding: 16px !important;
            position: relative !important;
            background: #${Skipper.hotel.primaryColor} !important;
            color: #ffffff !important;
            margin-top: 16px;
            border-radius: 8px;
          }
          .wide-logo-container {
            width: fit-content !important;
            margin: 0 auto 8px !important;
          }
          .checkbox-container {
            width: fit-content !important ;
            position: relative  !important;
            top: -10px  !important;
          }
          .gb-form-control-label {
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            margin-left: -11px;
            margin-right: 16px;
            vertical-align: middle;
            -webkit-tap-highlight-color: transparent;
            padding: 5px;
          }
          .gb-form-control-label svg {
            display: none;
          }
          .gb-form-control-label > span:nth-child(1) {
            padding: 5px;
          }
          .nonMemberMessageContainer, .memberMessageContainer {
            max-width: none !important;
            position: relative !important;
            top: -8px !important;
            font-size: 16px;
            font-weight: 400 !important;
            color: #ffffff !important;
            font-family: "Mark Pro", sans-serif, "Helvetica Neue", sans-serif;
          }
          .memberMessageContainer {
            top: 0px !important;
          }
          .partner-hotels {
            word-break: initial;
            color: #ffffff !important;
            text-decoration: underline !important;
            border-bottom: none !important;
          }
          .terms {
            text-decoration: underline !important;
            border-bottom: none !important;
            color: #ffffff !important;
            font-size: 12px;
          }
        </style>`
      );
    }
  },
  // Widget still shows up on the confirmation page and needs to be removed
  additionalConfirmationPageFunctions: () => {
    if (
      Skipper.shadowRoot.querySelectorAll("#gb-override-styles-confirmation-page")
        .length === 0
    ) {
      Skipper.shadowRoot.querySelector('main').insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-confirmation-page" type="text/css">
          .confirmation-container {
            font-family: "Mark Pro", sans-serif, "Helvetica Neue", sans-serif;
            display: flex !important;
            background: #${Skipper.hotel.primaryColor} !important;
            color: #ffffff !important;
            padding: 16px !important;
            border-radius: 8px;
          }
          .logo-container {
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
          }
          .logo-container svg {
            height: 45px !important;
            width: 45px !important;
          }
          .confirmation-text {
            text-align: left !important;
            margin-left: 16px !important;
            line-height: 20px !important;
            font-size: 13px !important;
            font-weight: 400 !important;
          }
          .confirmation-text a {
            color: #ffffff !important;
            text-decoration: underline !important;
            border-bottom: none !important;
          }
          .guestbook-confirm {
            margin-top: 16px; 
          }
        </style>`
      );
    }
    if (
      document.querySelectorAll('#gb-override-styles-confirmation-page')
        .length === 0
    ) {
      document.head.insertAdjacentHTML(
        "beforeend",
        `<style id="gb-override-styles-confirmation-page" type="text/css">
           #guestbook-widget-desktop, #guestbook-widget {
             display: none;
           }
         </style>`
      );
    }
  }
};

export default Skipper;
